import React, { useEffect, useMemo, useState } from 'react';
import { api } from './api';
import { useAuth } from './AuthContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import {
  BookOpen, PlayCircle, CheckCircle2, Circle, Clock, GraduationCap,
  Video, Sparkles, Home as HomeIcon, Building2, ChevronRight, Pencil,
  Award, Download, Plus, Trash2, Settings2, ExternalLink,
} from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import { useTabParam } from './shared/useTabParam';
import NumberInput from './shared/NumberInput';
import PickSelect from './shared/PickSelect';
import HelpSupport from './training/HelpSupport';
import HelpAsk from './training/HelpAsk';
import TeamProgress from './training/TeamProgress';

/* ------------------------------------------------------------------ helpers */

/** Turn any YouTube / Loom / Vimeo URL into an embeddable iframe URL. */
function toEmbedUrl(raw) {
  if (!raw) return null;
  try {
    const u = new URL(raw);
    const host = u.hostname.replace('www.', '');
    // YouTube
    if (host === 'youtu.be') return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    if (host.endsWith('youtube.com')) {
      const id = u.searchParams.get('v');
      if (id) return `https://www.youtube.com/embed/${id}`;
      // /embed/<id> already
      if (u.pathname.startsWith('/embed/')) return raw;
    }
    // Loom
    if (host.endsWith('loom.com')) {
      const parts = u.pathname.split('/').filter(Boolean);
      const id = parts[parts.length - 1];
      return `https://www.loom.com/embed/${id}`;
    }
    // Vimeo
    if (host.endsWith('vimeo.com')) {
      const parts = u.pathname.split('/').filter(Boolean);
      const id = parts[parts.length - 1];
      return `https://player.vimeo.com/video/${id}`;
    }
  } catch { /* fall through */ }
  return raw;
}

const PROVIDER_OPTIONS = [
  { value: 'youtube', label: 'YouTube' },
  { value: 'loom', label: 'Loom' },
  { value: 'vimeo', label: 'Vimeo' },
];

/** Provider implied by a pasted video URL's host, or null when it is not one of ours. */
export function providerFromUrl(raw) {
  try {
    const host = new URL(raw).hostname.replace('www.', '');
    if (host === 'youtu.be' || host.endsWith('youtube.com')) return 'youtube';
    if (host.endsWith('loom.com')) return 'loom';
    if (host.endsWith('vimeo.com')) return 'vimeo';
  } catch { /* not a URL yet: the user is still typing */ }
  return null;
}

/** Very small markdown-ish renderer: **bold**, `code`, bullet list, numbered list, blank lines. */
function LessonBody({ text }) {
  const blocks = String(text || '').split(/\n{2,}/).map((s) => s.trim()).filter(Boolean);
  const renderInline = (line) => {
    const parts = [];
    let i = 0;
    const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
    let m;
    while ((m = re.exec(line)) !== null) {
      if (m.index > i) parts.push(line.slice(i, m.index));
      const chunk = m[0];
      if (chunk.startsWith('**')) parts.push(<strong key={parts.length}>{chunk.slice(2, -2)}</strong>);
      else parts.push(<code key={parts.length} className="px-1.5 py-0.5 rounded bg-slate-100 text-[13px] font-mono text-slate-800">{chunk.slice(1, -1)}</code>);
      i = m.index + chunk.length;
    }
    if (i < line.length) parts.push(line.slice(i));
    return parts;
  };
  return (
    <div className="space-y-3 text-[15px] leading-relaxed text-slate-700">
      {blocks.map((b, i) => {
        const lines = b.split('\n');
        // numbered list
        if (lines.every((l) => /^\s*\d+\./.test(l))) {
          return (
            <ol key={i} className="list-decimal ml-6 space-y-1.5">
              {lines.map((l, j) => (<li key={j}>{renderInline(l.replace(/^\s*\d+\.\s*/, ''))}</li>))}
            </ol>
          );
        }
        // bullet list
        if (lines.every((l) => /^\s*-\s/.test(l))) {
          return (
            <ul key={i} className="list-disc ml-6 space-y-1.5">
              {lines.map((l, j) => (<li key={j}>{renderInline(l.replace(/^\s*-\s*/, ''))}</li>))}
            </ul>
          );
        }
        return <p key={i}>{lines.map((ln, j) => (<span key={j}>{renderInline(ln)}{j < lines.length - 1 && <br />}</span>))}</p>;
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ page  */

export default function TrainingCenterPage() {
  const { user } = useAuth();
  // Mirrors backend ACTION_KEYS["training.manage"] -> staff.manage.
  const isAdmin = !!(user?.is_super_admin || (user?.permissions || []).includes('staff.manage'));

  const [tracks, setTracks] = useState([]);
  const [activeTrackId, setActiveTrackId] = useTabParam('mortgage');   // ?tab=<track_id> (ticket 81)
  const [trackDetail, setTrackDetail] = useState(null);
  const [progress, setProgress] = useState({ completed_lessons: [], last_lesson_id: null });
  const [activeLessonId, setActiveLessonId] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [videoEditor, setVideoEditor] = useState(null); // {video_slot, url, provider}
  const [saving, setSaving] = useState(false);

  const completedSet = useMemo(() => new Set(progress.completed_lessons || []), [progress]);

  const loadTracks = async () => {
    try {
      const { data } = await api.get('/v2/training/tracks');
      setTracks(data.tracks || []);
    } catch (e) { toast.error('Could not load training tracks'); }
  };
  const loadProgress = async () => {
    try {
      const { data } = await api.get('/v2/training/progress');
      setProgress(data || { completed_lessons: [] });
    } catch { /* new user has no progress yet */ }
  };
  const loadTrackDetail = async (tid) => {
    try {
      const { data } = await api.get(`/v2/training/tracks/${tid}`);
      setTrackDetail(data);
    } catch { toast.error('Failed to load track'); }
  };
  const loadLesson = async (lid) => {
    try {
      const { data } = await api.get(`/v2/training/lesson/${lid}`);
      setLesson(data);
    } catch { toast.error('Failed to load lesson'); }
  };

  useEffect(() => { loadTracks(); loadProgress(); }, []);
  // A stale or mistyped ?tab= falls back to the first track instead of an empty page.
  useEffect(() => {
    if (tracks.length && !tracks.some((t) => t.track_id === activeTrackId)) setActiveTrackId(tracks[0].track_id);
  }, [tracks, activeTrackId, setActiveTrackId]);
  useEffect(() => { loadTrackDetail(activeTrackId); setActiveLessonId(null); setLesson(null); }, [activeTrackId]);
  useEffect(() => {
    if (activeLessonId) loadLesson(activeLessonId);
    else setLesson(null);
  }, [activeLessonId]);

  // Auto-select first lesson (or resume last one) once track detail is loaded
  useEffect(() => {
    if (!trackDetail || activeLessonId) return;
    // Try resume
    const last = progress?.last_lesson_id;
    const flat = trackDetail.modules.flatMap((m) => m.lessons.map((l) => l.lesson_id));
    if (last && flat.includes(last)) setActiveLessonId(last);
    else if (flat.length > 0) setActiveLessonId(flat[0]);
  }, [trackDetail]);

  const markComplete = async () => {
    if (!activeLessonId) return;
    try {
      await api.post(`/v2/training/lesson/${activeLessonId}/complete`);
      toast.success('Lesson marked complete');
      await Promise.all([loadProgress(), loadTracks(), loadTrackDetail(activeTrackId)]);
    } catch { toast.error('Could not mark complete'); }
  };

  // Jump to next lesson (across modules)
  const nextLesson = () => {
    if (!trackDetail) return null;
    const flat = trackDetail.modules.flatMap((m) => m.lessons.map((l) => l.lesson_id));
    const idx = flat.indexOf(activeLessonId);
    return idx >= 0 && idx < flat.length - 1 ? flat[idx + 1] : null;
  };

  const saveVideo = async () => {
    if (!videoEditor?.video_slot || !videoEditor?.url) return;
    setSaving(true);
    try {
      await api.post('/v2/training/videos', {
        video_slot: videoEditor.video_slot,
        url: videoEditor.url,
        provider: videoEditor.provider || 'youtube',
      });
      toast.success('Video URL saved');
      setVideoEditor(null);
      await Promise.all([loadTrackDetail(activeTrackId), activeLessonId && loadLesson(activeLessonId)]);
    } catch { toast.error('Failed to save the video URL'); }
    setSaving(false);
  };

  /* ------------- CERTIFICATE ------------- */
  const activeTrack = tracks.find((t) => t.track_id === activeTrackId);
  const certReady = !!activeTrack?.certificate_ready;
  const [cert, setCert] = useState(null);
  useEffect(() => {
    if (!certReady) { setCert(null); return; }
    api.get(`/v2/training/certificate/${activeTrackId}`)
      .then(({ data }) => setCert(data))
      .catch(() => setCert(null));
  }, [certReady, activeTrackId]);

  const downloadCertificate = async () => {
    try {
      const res = await api.get(`/v2/training/certificate/${activeTrackId}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `BrokerForYou-Certificate-${activeTrackId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error('Certificate download failed'); }
  };

  /* ------------- CUSTOM LESSONS (admin) ------------- */
  const [customMgrOpen, setCustomMgrOpen] = useState(false);
  const [customList, setCustomList] = useState([]);
  const [customEditor, setCustomEditor] = useState(null); // form state
  const [customSaving, setCustomSaving] = useState(false);

  const loadCustom = async () => {
    try {
      const { data } = await api.get(`/v2/training/custom-lessons?track_id=${activeTrackId}`);
      setCustomList(data.items || []);
    } catch { /* non-admin might get 200 empty; ignore */ }
  };
  useEffect(() => { if (customMgrOpen) loadCustom(); }, [customMgrOpen, activeTrackId]);

  const openCreateCustom = () => setCustomEditor({
    lesson_id: null, track_id: activeTrackId, module_name: '',
    title: '', body: '', minutes: 5, video_url: '', provider: 'youtube', order: 0,
  });
  const openEditCustom = (c) => setCustomEditor({ ...c });

  const saveCustom = async () => {
    if (!customEditor?.title || !customEditor?.module_name) {
      toast.error('Module name and title are required'); return;
    }
    setCustomSaving(true);
    try {
      if (customEditor.lesson_id) {
        // update
        const { lesson_id, tenant_id, created_at, updated_at, created_by, track_id, ...patch } = customEditor;
        await api.patch(`/v2/training/custom-lessons/${lesson_id}`, patch);
      } else {
        await api.post('/v2/training/custom-lessons', {
          track_id: customEditor.track_id, module_name: customEditor.module_name,
          title: customEditor.title, body: customEditor.body, minutes: Number(customEditor.minutes) || 5,
          video_url: customEditor.video_url || null, provider: customEditor.provider || 'youtube',
          order: Number(customEditor.order) || 0,
        });
      }
      toast.success('Lesson saved');
      setCustomEditor(null);
      await Promise.all([loadCustom(), loadTrackDetail(activeTrackId), loadTracks()]);
    } catch { toast.error('Save failed'); }
    setCustomSaving(false);
  };

  const deleteCustom = async (lesson_id) => {
    if (!(await confirmDialog({ title: 'Delete this custom lesson?', description: 'This cannot be undone.', confirmText: 'Delete', destructive: true }))) return;
    try {
      await api.delete(`/v2/training/custom-lessons/${lesson_id}`);
      toast.success('Deleted');
      await Promise.all([loadCustom(), loadTrackDetail(activeTrackId), loadTracks()]);
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6" data-testid="training-center-page">
      {/* Hero header */}
      <div className="rounded-2xl overflow-hidden mb-6 relative"
           style={{ background: 'linear-gradient(135deg, #070B5F 0%, #101c8a 55%, #6BA82E 140%)' }}>
        <div className="px-6 md:px-8 py-6 md:py-8 flex items-center gap-5 text-white">
          <div className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0"
               style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(6px)' }}>
            <GraduationCap size={28} />
          </div>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-[0.2em] text-white/60 font-semibold">Training Center</div>
            <h1 className="text-2xl md:text-3xl font-bold mt-1">Learn BrokerForYou at your own pace</h1>
            <p className="text-white/70 mt-1 text-sm md:text-base">
              Two guided tracks — Mortgage and Real Estate — each with hands-on lessons, video walkthroughs, and progress tracking.
            </p>
          </div>
          <div className="hidden md:flex items-center gap-2 text-white/80 text-xs">
            <Sparkles size={14} className="text-[#A6E56C]" />
            <span>Progress saved automatically</span>
          </div>
        </div>
      </div>

      <HelpAsk />
      <HelpSupport />
      {isAdmin && <TeamProgress />}

      {/* Track cards */}
      <Tabs value={activeTrackId} onValueChange={setActiveTrackId} className="w-full">
        <TabsList className="mb-4" data-testid="training-track-tabs">
          <TabsTrigger value="mortgage" data-testid="track-tab-mortgage" className="gap-2">
            <HomeIcon size={14} /> Mortgage
          </TabsTrigger>
          <TabsTrigger value="real-estate" data-testid="track-tab-real-estate" className="gap-2">
            <Building2 size={14} /> Real Estate
          </TabsTrigger>
        </TabsList>

        {/* Summary strip */}
        <div className="grid md:grid-cols-2 gap-3 mb-6">
          {tracks.map((t) => {
            const pct = t.lessons_count ? Math.round((t.lessons_completed / t.lessons_count) * 100) : 0;
            const active = activeTrackId === t.track_id;
            return (
              <Card key={t.track_id}
                    onClick={() => setActiveTrackId(t.track_id)}
                    data-testid={`track-summary-${t.track_id}`}
                    className={`cursor-pointer p-4 transition-all border ${active ? 'border-[#6BA82E] shadow-md' : 'border-slate-200 hover:border-slate-300'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                      {t.track_id === 'mortgage' ? 'Mortgage Broker' : 'Real Estate Agent'}
                    </div>
                    <div className="font-bold text-slate-900 mt-0.5">{t.name}</div>
                    <div className="text-xs text-slate-500 mt-1 flex items-center gap-3">
                      <span className="inline-flex items-center gap-1"><Clock size={11} /> ~{t.estimated_minutes} min</span>
                      <span>{t.modules_count} modules · {t.lessons_count} lessons</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <Badge variant="secondary" className="bg-slate-900 text-white">{pct}%</Badge>
                    {t.certificate_ready && (
                      <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 gap-1" data-testid={`cert-badge-${t.track_id}`}>
                        <Award size={11} /> Certificate
                      </Badge>
                    )}
                  </div>
                </div>
                <Progress value={pct} className="h-1.5 mt-3" />
              </Card>
            );
          })}
        </div>

        {/* Two-column: modules list (left) + lesson viewer (right) */}
        <TabsContent value={activeTrackId} className="mt-0">
          {/* Certificate celebration banner */}
          {certReady && cert && (
            <div className="rounded-xl mb-5 p-4 md:p-5 flex items-center gap-4 relative overflow-hidden"
                 data-testid="cert-ready-banner"
                 style={{ background: 'linear-gradient(90deg, #052E1B 0%, #0F6B3D 60%, #6BA82E 130%)' }}>
              <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
                   style={{ background: 'rgba(255,255,255,0.15)' }}>
                <Award size={24} className="text-white" />
              </div>
              <div className="flex-1 text-white">
                <div className="font-bold text-lg">🎓 Congratulations, {cert.user_name?.split(' ')[0] || 'you'}!</div>
                <div className="text-white/80 text-sm">
                  You completed the {cert.track_name}. Your certificate is ready to download and share.
                  <span className="ml-2 text-white/60 text-xs">Verification code: <b className="text-white/90">{cert.verification_code}</b></span>
                  <a href={`/verify-certificate/${encodeURIComponent(cert.verification_code)}`} target="_blank" rel="noreferrer"
                     data-testid="cert-verify-link"
                     className="ml-2 text-xs text-white underline underline-offset-2 hover:text-white/80">Public verification page</a>
                </div>
              </div>
              <Button onClick={downloadCertificate} data-testid="download-cert-btn"
                      className="bg-white text-emerald-900 hover:bg-white/90 shrink-0 shadow">
                <Download size={16} className="mr-1.5" /> Download PDF
              </Button>
            </div>
          )}

          {/* Admin: manage custom lessons */}
          {isAdmin && (
            <div className="mb-4 flex justify-end">
              <Button variant="outline" size="sm" onClick={() => setCustomMgrOpen(true)}
                      data-testid="manage-custom-btn">
                <Settings2 size={14} className="mr-1.5" />
                Manage custom lessons for this track
              </Button>
            </div>
          )}
          <div className="grid lg:grid-cols-[340px_1fr] gap-5">

            {/* LEFT — modules */}
            <div className="space-y-4" data-testid="training-modules-list">
              {trackDetail?.modules?.map((m, mi) => (
                <Card key={m.module_id} className="p-3 border border-slate-200">
                  <div className="flex items-center justify-between mb-2 px-1">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold text-white"
                           style={{ background: '#070B5F' }}>{mi + 1}</div>
                      <div className="text-sm font-semibold text-slate-900">{m.name}</div>
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                      {m.lessons.filter((l) => l.completed).length}/{m.lessons.length}
                    </div>
                  </div>
                  <div className="space-y-1">
                    {m.lessons.map((l) => {
                      const active = l.lesson_id === activeLessonId;
                      return (
                        <button
                          key={l.lesson_id}
                          onClick={() => setActiveLessonId(l.lesson_id)}
                          data-testid={`lesson-btn-${l.lesson_id}`}
                          className={`w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-md transition-all group
                                      ${active ? 'bg-[#070B5F] text-white'
                                              : 'hover:bg-slate-50 text-slate-700'}`}
                        >
                          {l.completed
                            ? <CheckCircle2 size={16} className={active ? 'text-[#A6E56C]' : 'text-emerald-600'} />
                            : <Circle size={16} className={active ? 'text-white/60' : 'text-slate-300'} />}
                          <span className="flex-1 text-sm">{l.title}</span>
                          <span className={`text-[10px] ${active ? 'text-white/70' : 'text-slate-400'}`}>{l.minutes}m</span>
                          <ChevronRight size={12} className={active ? 'text-white/70' : 'text-slate-300 group-hover:text-slate-500'} />
                        </button>
                      );
                    })}
                  </div>
                </Card>
              ))}
            </div>

            {/* RIGHT — lesson viewer */}
            <div className="min-w-0" data-testid="lesson-viewer">
              {!lesson ? (
                <Card className="p-10 text-center text-slate-500 border-dashed">
                  <BookOpen className="mx-auto mb-3 text-slate-300" size={40} />
                  Select a lesson from the left to begin.
                </Card>
              ) : (
                <div className="space-y-4">
                  {/* Video panel */}
                  <Card className="overflow-hidden border border-slate-200">
                    <div className="aspect-video bg-slate-900 relative flex items-center justify-center" data-testid="lesson-video-panel">
                      {lesson.video?.url ? (
                        <iframe
                          key={lesson.video.url}
                          src={toEmbedUrl(lesson.video.url)}
                          title={lesson.title}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="w-full h-full"
                        />
                      ) : (
                        <div className="text-center px-6">
                          <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-3"
                               style={{ background: 'rgba(255,255,255,0.08)' }}>
                            <Video size={26} className="text-white/70" />
                          </div>
                          <div className="text-white/90 font-semibold mb-1">Video coming soon</div>
                          <div className="text-white/50 text-sm">
                            {isAdmin
                              ? 'Click "Set Video URL" below to embed a YouTube, Loom, or Vimeo link.'
                              : 'Your admin will add a walkthrough here.'}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-between px-4 py-3 bg-white">
                      <div className="text-xs text-slate-500 flex items-center gap-3">
                        <span className="inline-flex items-center gap-1"><Clock size={12} /> {lesson.minutes} min</span>
                        {lesson.video?.provider && <span className="uppercase text-[10px] tracking-widest">{lesson.video.provider}</span>}
                      </div>
                      {isAdmin && (
                        <Button variant="outline" size="sm"
                                onClick={() => setVideoEditor({ video_slot: lesson.video_slot, url: lesson.video?.url || '', provider: lesson.video?.provider || 'youtube' })}
                                data-testid="set-video-url-btn">
                          <Pencil size={14} className="mr-1.5" />
                          {lesson.video?.url ? 'Update Video URL' : 'Set Video URL'}
                        </Button>
                      )}
                    </div>
                  </Card>

                  {/* Lesson body */}
                  <Card className="p-6 border border-slate-200">
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div>
                        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                          Lesson {lesson.lesson_id.toUpperCase()}
                        </div>
                        <h2 className="text-2xl font-bold text-slate-900 mt-0.5">{lesson.title}</h2>
                      </div>
                      {lesson.completed
                        ? <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200"><CheckCircle2 size={13} className="mr-1" /> Completed</Badge>
                        : <Badge variant="outline" className="text-slate-600">Not started</Badge>}
                    </div>
                    <LessonBody text={lesson.body} />

                    <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between">
                      <Button variant="ghost" onClick={markComplete} data-testid="mark-complete-btn"
                              className="text-emerald-700 hover:bg-emerald-50">
                        <CheckCircle2 size={16} className="mr-1.5" />
                        {lesson.completed ? 'Marked complete' : 'Mark lesson complete'}
                      </Button>
                      <Button
                        onClick={() => {
                          const nxt = nextLesson();
                          if (nxt) setActiveLessonId(nxt);
                          else toast.success('You reached the end of the track!');
                        }}
                        data-testid="next-lesson-btn"
                        style={{ background: '#070B5F' }} className="text-white hover:opacity-95">
                        <PlayCircle size={16} className="mr-1.5" />
                        Next lesson
                      </Button>
                    </div>
                  </Card>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Admin video URL editor */}
      <Dialog open={!!videoEditor} onOpenChange={(v) => !v && setVideoEditor(null)}>
        <DialogContent data-testid="video-editor-dialog">
          <DialogHeader>
            <DialogTitle>Set video URL</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Lesson slot</Label>
              <Input value={videoEditor?.video_slot || ''} disabled />
            </div>
            <div>
              <Label>Video URL (YouTube / Loom / Vimeo)</Label>
              <Input
                value={videoEditor?.url || ''}
                onChange={(e) => setVideoEditor((v) => ({ ...v, url: e.target.value, provider: providerFromUrl(e.target.value) || v.provider }))}
                placeholder="https://www.youtube.com/watch?v=…"
                data-testid="video-url-input"
              />
              <div className="text-[11px] text-slate-500 mt-1">Paste any YouTube / Loom / Vimeo link — we'll auto-convert to embed format and pick the provider.</div>
            </div>
            <PickSelect
              label="Provider"
              labelClassName="text-sm font-medium leading-none"
              value={videoEditor?.provider || 'youtube'}
              onChange={(provider) => setVideoEditor((v) => ({ ...v, provider }))}
              options={PROVIDER_OPTIONS}
              testId="video-provider-select"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVideoEditor(null)}>Cancel</Button>
            <Button onClick={saveVideo} disabled={saving || !videoEditor?.url}
                    style={{ background: '#070B5F' }} className="text-white"
                    data-testid="save-video-url-btn">
              {saving ? 'Saving…' : 'Save video URL'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin — Manage tenant custom lessons */}
      <Dialog open={customMgrOpen} onOpenChange={setCustomMgrOpen}>
        <DialogContent className="max-w-3xl" data-testid="custom-mgr-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 size={18} /> Custom lessons — {activeTrackId === 'mortgage' ? 'Mortgage' : 'Real Estate'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-500">
              Add your own onboarding lessons visible only to your tenant. Group them under any module name.
            </div>
            <div className="border border-slate-200 rounded-md divide-y max-h-72 overflow-auto">
              {customList.length === 0 ? (
                <div className="p-6 text-center text-slate-400 text-sm">No custom lessons yet.</div>
              ) : customList.map((c) => (
                <div key={c.lesson_id} className="p-3 flex items-center gap-3" data-testid={`custom-row-${c.lesson_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{c.title}</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {c.module_name} · {c.minutes}m {c.video_url && <span className="ml-1 inline-flex items-center gap-0.5"><Video size={10} /> video</span>}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => openEditCustom(c)} data-testid={`custom-edit-${c.lesson_id}`}>
                    <Pencil size={13} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteCustom(c.lesson_id)}
                          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                          data-testid={`custom-delete-${c.lesson_id}`}>
                    <Trash2 size={13} />
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex justify-end">
              <Button onClick={openCreateCustom} data-testid="new-custom-btn"
                      style={{ background: '#070B5F' }} className="text-white">
                <Plus size={14} className="mr-1.5" /> New custom lesson
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin — Custom lesson editor (create + edit) */}
      <Dialog open={!!customEditor} onOpenChange={(v) => !v && setCustomEditor(null)}>
        <DialogContent className="max-w-2xl" data-testid="custom-editor-dialog">
          <DialogHeader>
            <DialogTitle>{customEditor?.lesson_id ? 'Edit custom lesson' : 'New custom lesson'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Module (grouping)</Label>
              <Input
                value={customEditor?.module_name || ''}
                onChange={(e) => setCustomEditor((v) => ({ ...v, module_name: e.target.value }))}
                placeholder="Company Playbook"
                data-testid="custom-module-input"
              />
            </div>
            <div>
              <Label>Order</Label>
              <NumberInput integer
                value={customEditor?.order ?? 0}
                onChange={(n) => setCustomEditor((v) => ({ ...v, order: n ?? '' }))}
              />
            </div>
            <div className="col-span-2">
              <Label>Title</Label>
              <Input
                value={customEditor?.title || ''}
                onChange={(e) => setCustomEditor((v) => ({ ...v, title: e.target.value }))}
                placeholder="Our offer strategy"
                data-testid="custom-title-input"
              />
            </div>
            <div>
              <Label>Minutes</Label>
              <NumberInput integer min={1}
                value={customEditor?.minutes ?? 5}
                onChange={(n) => setCustomEditor((v) => ({ ...v, minutes: n ?? '' }))}
              />
            </div>
            <PickSelect
              label="Provider"
              labelClassName="text-sm font-medium leading-none"
              value={customEditor?.provider || 'youtube'}
              onChange={(provider) => setCustomEditor((v) => ({ ...v, provider }))}
              options={PROVIDER_OPTIONS}
              testId="custom-provider-select"
            />
            <div className="col-span-2">
              <Label>Video URL (optional)</Label>
              <Input
                value={customEditor?.video_url || ''}
                onChange={(e) => setCustomEditor((v) => ({ ...v, video_url: e.target.value, provider: providerFromUrl(e.target.value) || v.provider }))}
                placeholder="https://www.youtube.com/watch?v=…"
                data-testid="custom-video-input"
              />
            </div>
            <div className="col-span-2">
              <Label>Lesson body</Label>
              <Textarea rows={8}
                value={customEditor?.body || ''}
                onChange={(e) => setCustomEditor((v) => ({ ...v, body: e.target.value }))}
                placeholder="Supports **bold**, `code`, - bullet lists, and 1. numbered lists"
                data-testid="custom-body-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomEditor(null)}>Cancel</Button>
            <Button onClick={saveCustom} disabled={customSaving}
                    style={{ background: '#070B5F' }} className="text-white"
                    data-testid="save-custom-btn">
              {customSaving ? 'Saving…' : (customEditor?.lesson_id ? 'Save changes' : 'Create lesson')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
